import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FileText, Save, RefreshCw, Loader2, AlertCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface ExtractedContentEditorProps {
  sessionId: string;
  extractedText: string;
  contentType: string;
  contentTypeLabel?: string;
  onContentUpdated: (newText: string) => void;
  onRegenerate: () => Promise<void>;
}

export function ExtractedContentEditor({
  sessionId,
  extractedText,
  contentType,
  contentTypeLabel,
  onContentUpdated,
  onRegenerate,
}: ExtractedContentEditorProps) {
  const [editedText, setEditedText] = useState(extractedText);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleTextChange = (newText: string) => {
    setEditedText(newText);
    setHasUnsavedChanges(newText !== extractedText);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("smartfile_sessions")
        .update({ 
          extracted_text: editedText,
          updated_at: new Date().toISOString()
        })
        .eq("id", sessionId);

      if (error) throw error;

      onContentUpdated(editedText);
      setHasUnsavedChanges(false);
      toast.success("Conteúdo salvo com sucesso!");
    } catch (error: any) {
      console.error("Error saving content:", error);
      toast.error("Erro ao salvar conteúdo");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (hasUnsavedChanges) {
      toast.error("Salve as alterações antes de regerar registros");
      return;
    }

    setIsRegenerating(true);
    try {
      await onRegenerate();
      toast.success("Registros regenerados com sucesso!");
    } catch (error: any) {
      console.error("Error regenerating:", error);
      toast.error("Erro ao regerar registros");
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-primary/20">
        <CardHeader>
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer hover:bg-muted/50 -m-6 p-6 rounded-t-lg transition-colors">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle>
                  {contentTypeLabel ? `${contentTypeLabel} - Conteúdo Extraído` : '📄 Conteúdo Extraído'}
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <div onClick={(e) => e.stopPropagation()}>
                  {hasUnsavedChanges && (
                    <Badge variant="outline" className="text-amber-600 border-amber-600">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Alterações não salvas
                    </Badge>
                  )}
                </div>
                <ChevronDown 
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                    isOpen ? 'transform rotate-180' : ''
                  }`}
                />
              </div>
            </div>
          </CollapsibleTrigger>
          <CardDescription className="pt-2">
            {isOpen 
              ? "Revise e edite o texto extraído do documento. Após salvar, você pode regerar os registros estruturados."
              : "Clique para expandir e editar o conteúdo extraído"
            }
          </CardDescription>
        </CardHeader>
        
        <CollapsibleContent>
          <CardContent className="space-y-4">
            <Textarea
              value={editedText}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder="Conteúdo extraído do documento..."
              className="min-h-[300px] font-mono text-sm"
              disabled={isSaving || isRegenerating}
            />

            <div className="flex flex-col lg:flex-row gap-3">
              <Button
                onClick={handleSave}
                disabled={!hasUnsavedChanges || isSaving || isRegenerating}
                className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>

              <Button
                onClick={handleRegenerate}
                disabled={hasUnsavedChanges || isSaving || isRegenerating}
                variant="outline"
                className="border-secondary text-secondary hover:bg-secondary/10 w-full sm:w-auto"
              >
                {isRegenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Regenerando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regerar Registros
                  </>
                )}
              </Button>
            </div>

            {hasUnsavedChanges && (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                Salve as alterações antes de regerar os registros
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
